import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { BarStack, Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import _ from 'lodash';

import {
  getParams,
  thousandFormat,
  thousandFormat2,
} from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  minusActiveColor,
  minusDisableColor,
} from '../../../../Assets/js/constant';

// accessors
const x = (d) => d.FY;

let tooltipTimeout;

const TopChart = ({
  summaryData,
  selectedTopItems,
  selectedLabels,
  handleFilter,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const isMountedRef = useRef(false);
  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [budgets, setBudgets] = useState([]);

  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prevResize => !prevResize);
    }
  }, []);

  const prepareData = useCallback(() => {
    let data = [],
      forecastdata = [],
      totals = [],
      budgets = [];

    selectedTopItems.forEach((cur) => {
      data.push({
        FY: cur.year,
        Profit: 0,
        Budget: 0,
      });
      forecastdata.push({
        FY: cur.year,
        Profit: 0,
        Budget: 0,
      });
      totals.push(0);
      budgets.push(0);
    });

    summaryData.forEach((item) => {
      if (item.ActualFlag) {
        let idx = _.findIndex(data, { FY: item.FY });
        data[idx] = item;
      } else {
        forecastdata.map((fd) => {
          if (fd.FY === item.FY) {
            fd.Profit += item.Profit;
            fd.Budget += item.Budget;
          }
          return fd;
        });
      }
    });

    data.map((d) => {
      if (d.Profit === 0) {
        let forecast = forecastdata.find((fd) => fd.FY === d.FY);
        d.Profit = forecast.Profit;
        d.Budget = forecast.Budget;
      }
      return d;
    });

    data.forEach((item) => {
      let idx = _.findIndex(data, { FY: item.FY });
      data[idx] = item;
      if (item.Profit !== undefined) totals[idx] = parseFloat(item.Profit);
      if (item.Budget !== undefined) budgets[idx] = parseFloat(item.Budget);
    });

    setData(data);
    setTotals(totals);
    setBudgets(budgets);
  }, [summaryData, selectedTopItems]);

  useEffect(() => {
    isMountedRef.current = true;
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (isMountedRef.current) {
      prepareData();
    }
  }, [summaryData, selectedTopItems, prepareData]);

  const getColor = useCallback((bar) => {
    const year = bar.bar.data.FY;

    const actual = bar.bar.data.Profit ? bar.bar.data.Profit : 0;
    const budget = bar.bar.data.Budget ? bar.bar.data.Budget : 0;

    let activeColor =
      actual < budget ? negativeActiveColor : positiveActiveColor;
    let disableColor =
      actual < budget ? negativeDisableColor : positiveDisableColor;
    if (actual < 0) {
      activeColor = minusActiveColor;
      disableColor = minusDisableColor;
    }

    if (selectedTopItems.length === 0) {
      return activeColor;
    }

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (selectedTopItems[i].year === year) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedTopItems]);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedTopItems, _selectedLabels;

    const year = bar.bar.data.FY;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopItems = selectedTopItems.slice();
      _selectedLabels = selectedLabels.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedTopItems.push({ year: year });
        _selectedLabels.push(year);
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedTopItems.splice(index, 1);
        _selectedLabels.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedTopItems = [];
        _selectedBars = [];
        _selectedLabels = [];
      } else {
        _selectedTopItems = [{ year: year }];
        _selectedLabels = [year];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);

    handleFilter({
      selectedLabels: _selectedLabels,
      selectedTopItems: _selectedTopItems,
    });
  }, [selectedBars, selectedTopItems, selectedLabels, handleFilter]);

  const handleLabel = useCallback((event, year) => {
    let _selectedTopItems,
      _selectedBars,
      _selectedLabels = [];

    let index = NaN;
    for (let i = 0; i < selectedTopItems.length; i++) {
      if (selectedTopItems[i].year === year) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopItems = selectedTopItems.slice();
      _selectedLabels = selectedLabels.slice();
      if (isNaN(index)) {
        _selectedTopItems.push({ year: year });
        _selectedLabels.push(year);
      } else {
        _selectedTopItems.splice(index, 1);
        _selectedLabels.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedTopItems = [];
      _selectedLabels = [];
      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      if (!isNaN(index) && _selectedTopItems.length === 1) {
        _selectedTopItems = [];
        _selectedLabels = [];
      } else {
        _selectedTopItems = [{ year: year }];
        _selectedLabels = [year];
      }
    }

    setSelectedBars(_selectedBars);

    handleFilter({
      selectedTopItems: _selectedTopItems,
      selectedLabels: _selectedLabels,
    });
  }, [selectedTopItems, selectedLabels, selectedBars, handleFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('yfpTopItems');
    handleFilter({
      selectedTopItems: [],
      selectedLabels: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let tooltip_data = {};
    const coords = localPoint(event.target.ownerSVGElement, event);

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      tooltip_data = bar;
    } else {
      tooltip_data['budget'] = bar;
    }

    tooltip_data['isBar'] = isBar;

    // calculate mtdSum
    let selSum = 0;
    let mtdMonths = 0;

    if (data && selectedTopItems.length > 1)
      data.forEach((item) => {
        selectedTopItems.forEach((subitem) => {
          if (item.FY === subitem.year) {
            selSum += item.Profit;
            mtdMonths++;
          }
        });
      });

    tooltip_data['selSum'] = selSum;
    tooltip_data['items'] = mtdMonths;

    showTooltip({
      tooltipData: tooltip_data,
      tooltipTop: coords.y,
      tooltipLeft: coords.x,
    });
  }, [hoverBar, selectedTopItems, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);

      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  let xTransform = 0;
  let maxYValue = Math.max(
    Math.abs(Math.min(Math.min(...totals), Math.min(...budgets))),
    Math.max(Math.max(...totals), Math.max(...budgets)),
  );
  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (parseInt(thousandFormat(maxYValue)[0]) > 7)
      xTransform = 7.5 * (thousandFormat2(maxYValue).length + 1) + 5;
    else xTransform = 7.5 * thousandFormat2(maxYValue).length + 5;
  }

  const width = window.innerWidth;
  const height = 160;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );
  const offsetX = 3.5;
  const offsetWidth = -1.2;
  let minY = Math.min(Math.min(...totals), Math.min(...budgets));
  minY = Math.min(0, minY);
  let maxY = Math.max(Math.max(...totals), Math.max(...budgets));
  maxY = Math.max(0, maxY);

  // scales
  const xScale = scaleBand()
    .domain(data.map(x))
    .rangeRound([0, xMax])
    .padding(0.2);

  const yScale = scaleLinear({
    domain: [minY * 1.3, maxY * 1.3],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: ['Profit'],
    range: [positiveActiveColor],
  });

  return (
    <div>
      <div className="subtitle mb-10">Actual Profit vs Budget</div>

      <Grid container>
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
            onClick={deSelectAll}
          />

          <Group top={margin.top} left={margin.left}>
            <BarStack
              data={data}
              keys={['Profit']}
              x={x}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        x={bar.x}
                        y={
                          isNaN(bar.height)
                            ? 0
                            : bar.height <= 0
                            ? yScale(0)
                            : bar.y
                        }
                        height={
                          isNaN(bar.height)
                            ? 0
                            : bar.height < 0
                            ? bar.y - yScale(0)
                            : bar.height
                        }
                        width={bar.width}
                        fill={getColor(bar)}
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={(event) => hideTooltipHandler()}
                        onMouseMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                        onTouchEnd={(event) => hideTooltipHandler()}
                        onTouchMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                      />
                    );
                  });
                });
              }}
            </BarStack>

            {budgets.map((budget, index) => {
              return (
                <Group key={`bar-budget-${index}`}>
                  <Bar
                    width={
                      (xMax - xScale.paddingInner() * xScale.step()) /
                        budgets.length +
                      offsetWidth
                    }
                    height={1}
                    x={
                      (xScale.paddingInner() * xScale.step()) / 2 +
                      index * xScale.step() +
                      offsetX
                    }
                    y={isNaN(yScale(budget)) ? 0 : yScale(budget)}
                    fill="black"
                    stroke="black"
                    strokeWidth={1}
                    onMouseLeave={(event) => hideTooltipHandler()}
                    onMouseMove={(event) =>
                      showTooltipHandler(event, xScale, budget, false)
                    }
                    onTouchEnd={(event) => hideTooltipHandler()}
                    onTouchMove={(event) =>
                      showTooltipHandler(event, xScale, budget, false)
                    }
                  />
                </Group>
              );
            })}

            <Bar
              width={xMax}
              height={1}
              y={isNaN(yScale(0)) ? 0 : yScale(0)}
            />

            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />

            {data.map((item, index) => {
              return (
                <rect
                  key={index}
                  x={
                    (xScale.paddingInner() * xScale.step()) / 2 +
                    index * xScale.step() +
                    offsetX
                  }
                  y={yMax ? yMax : 0}
                  width={
                    (xMax - (xScale.paddingInner() * xScale.step()) / 2) /
                      data.length +
                    offsetWidth
                  }
                  height={margin.bottom}
                  fill="transparent"
                  onClick={(event) => handleLabel(event, item.FY)}
                />
              );
            })}

            <AxisBottom
              hideTicks={true}
              numTicks={6}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  style={{
                    fontWeight:
                      selectedLabels.indexOf(parseInt(formattedValue)) > -1
                        ? 'bold'
                        : 500,
                  }}
                  fill="black"
                  onClick={(event) =>
                    handleLabel(event, parseInt(formattedValue))
                  }
                >
                  FY {formattedValue}
                </text>
              )}
            />
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <div className="bkg-white color-black" style={{ padding: 0, margin: 0 }}>
                {tooltipData.selSum !== 0 ? (
                  <div
                    className="ft-12"
                    style={{
                      backgroundColor: '#dddddd',
                      margin: 0,
                      minHeight: 25,
                    }}
                  >
                    <div style={{ padding: 5 }}>
                      {tooltipData.items} items selected SUM(ProfitMTD
                      HistoricalOnly):&nbsp;
                      <strong>{thousandFormat2(tooltipData.selSum)}</strong>
                    </div>
                  </div>
                ) : null}
                <div style={{ margin: 5 }}>
                  <div className="pdv-10">
                    <strong>FY : {tooltipData.bar.data.FY}</strong>
                  </div>
                  <div className="ft-12">
                    Actual:{' '}
                    <strong>
                      {thousandFormat2(tooltipData.bar.data.Profit)}
                    </strong>
                  </div>
                  <div className="ft-12">
                    Budget:{' '}
                    <strong>
                      {thousandFormat2(tooltipData.bar.data.Budget)}
                    </strong>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bkg-white color-black">
                Sum ProfitMTDForecast HistoricalOnly ={' '}
                {thousandFormat2(tooltipData.budget)}
              </div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedLabels: PropTypes.array.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
